/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, useContext, useMemo } from 'react'

import semver from 'semver'

import { useAuth } from './AuthProvider'

// Feature Flag provider that we use to check if a feature is enabled
// based on the backend version that we pull based on the user's instance.

// Defin feature flags in the FeatureFlagService class under the flags object.
// Each feature flag has a name, description, enabledFrom

// ####### Usage #######
// const MyComponent = () => {
//   const newUIEnabled = useFeatureFlag(FeatureFlagName.NEW_UI)
//   const allEnabledFeatures = useAllEnabledFeatures()
//   return newUIEnabled ? <NewUIComponent /> : <OldUIComponent />
// }

// const MyComponent = () => {
//   const enabledFeatures = useAllEnabledFeatures()
//   return (
//     <>
//      {enabledFeatures.includes(FeatureFlagName.NEW_UI) && <NewUIComponent />}
//      {enabledFeatures.includes(FeatureFlagName.ADVANCED_SEARC) && <AdvancedSearchBar />}
//      {enabledFeatures.includes(FeatureFlagName.USER_PROFILES) && <UserProfileSection />}
//    </>
//   )

type FeatureFlag = {
  description: string
  enabledFrom: string
}

// NEW_UI = 'NEW_UI'
export enum FeatureFlagName {
  DeleteDestinations,
  WorkflowUpdate,
  AssignSourceToPipeline,
  SupportQueryingFirehoseNodeDirectly,
  CreateSourceAndLastSeen,
  QueryRailsLive,
}

class FeatureFlagService {
  private flags: Record<FeatureFlagName, FeatureFlag> = {
    // Example feature flag
    // NEW_UI: {
    //   description: 'New user interface components',
    //   enabledFrom: '1.0.0',
    // },
    // Add more feature flags here
    [FeatureFlagName.DeleteDestinations]: {
      description: 'delete destinations',
      enabledFrom: '1.50.0',
    },
    [FeatureFlagName.WorkflowUpdate]: {
      description: 'revamp of the pipeline view to include new styling and functionality',
      enabledFrom: '0.0.0', // v 1.90.0
    },
    [FeatureFlagName.AssignSourceToPipeline]: {
      description: 'assign source to pipeline in the new onboard flow',
      enabledFrom: '1.68.0',
    },
    [FeatureFlagName.SupportQueryingFirehoseNodeDirectly]: {
      description: 'support querying firehose node directly',
      enabledFrom: '1.73.0',
    },
    [FeatureFlagName.CreateSourceAndLastSeen]: {
      description: 'createSource and source queries to support improved onboarding flow',
      enabledFrom: '1.91.0',
    },
    [FeatureFlagName.QueryRailsLive]: {
      description: 'query rails REST requests for pipelines, teams, settings, managing access',
      enabledFrom: '1.122.0',
    },
  }

  private backendVersion: string
  // @ts-expect-error we dont want to ovrride all of our feature flags only
  // ones set within the constructor
  private localOverrides: Record<FeatureFlagName, boolean> = {}

  constructor(backendVersion: string) {
    this.backendVersion = backendVersion
    this.localOverrides[FeatureFlagName.WorkflowUpdate] = false
  }

  setLocalOverride(flagName: FeatureFlagName, enabled: boolean): void {
    this.localOverrides[flagName] = enabled
  }

  isFeatureEnabled(flagName: FeatureFlagName): boolean {
    if (flagName in this.localOverrides) {
      return this.localOverrides[flagName]
    }
    const flag = this.flags[flagName]
    if (!flag) return false
    return this.isBackendCompatible(flag.enabledFrom)
  }

  getAllEnabledFeatures(): string[] {
    return Object.entries(this.flags)
      .filter(([_, flag]) => this.isBackendCompatible(flag.enabledFrom))
      .map(([flagName, _]) => flagName)
  }

  private isBackendCompatible(version: string) {
    return semver.gte(this.backendVersion, version)
  }
}

const FeatureFlagContext = createContext<FeatureFlagService | null>(null)

type FeatureFlagProviderProps = {
  children: React.ReactNode
}

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const auth = useAuth()
  const { currentInstance } = auth
  const version = currentInstance?.version || '1.0.0'
  const featureFlagService = useMemo(() => new FeatureFlagService(version), [version])

  return <FeatureFlagContext.Provider value={featureFlagService}>{children}</FeatureFlagContext.Provider>
}

export const useSetLocalOverrideFeatureFlag = () => {
  const featureFlagService = useContext(FeatureFlagContext)
  if (!featureFlagService) {
    throw new Error('useSetLocalOverrideFeatureFlag must be used within a FeatureFlagProvider')
  }
  return (flagName: FeatureFlagName, enabled: boolean) => {
    featureFlagService.setLocalOverride(flagName, enabled)
  }
}

export const useGetLocalOverrideFeatureFlag = () => {
  const featureFlagService = useContext(FeatureFlagContext)
  if (!featureFlagService) {
    throw new Error('useGetLocalFeatureFlag must be used within a FeatureFlagProvider')
  }
  return (flagName: FeatureFlagName) => {
    return featureFlagService.isFeatureEnabled(flagName)
  }
}

export const useFeatureFlag = (flagName: FeatureFlagName): boolean => {
  const featureFlagService = useContext(FeatureFlagContext)
  if (!featureFlagService) {
    throw new Error('useFeatureFlag must be used within a FeatureFlagProvider')
  }
  return featureFlagService.isFeatureEnabled(flagName)
}

export const useAllEnabledFeatures = (): string[] => {
  const featureFlagService = useContext(FeatureFlagContext)
  if (!featureFlagService) {
    throw new Error('useAllEnabledFeatures must be used within a FeatureFlagProvider')
  }
  return featureFlagService.getAllEnabledFeatures()
}

export const useIsFeatureEnabled = (flagName: FeatureFlagName): boolean => {
  const featureFlagService = useContext(FeatureFlagContext)
  if (!featureFlagService) {
    throw new Error('useIsFeatureEnabled must be used within a FeatureFlagProvider')
  }
  return featureFlagService.isFeatureEnabled(flagName)
}
